import { useState, useEffect } from 'react';

/**
 * Hook pour détecter les media queries
 * @param query - La media query à surveiller (ex: '(min-width: 1024px)')
 * @returns boolean indiquant si la query match
 */
export const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState<boolean>(() => {
    // Vérifier si window est disponible (SSR safety)
    if (typeof window === 'undefined') {
      return false;
    }
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const mediaQuery = window.matchMedia(query);

    // Handler pour les changements
    const handleChange = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    // Set initial value
    setMatches(mediaQuery.matches);

    // Ajouter le listener
    mediaQuery.addEventListener('change', handleChange);

    // Cleanup
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [query]);

  return matches;
};

/**
 * Convenience hook pour détecter le mode desktop (≥1024px)
 * @returns boolean - true si viewport ≥1024px
 */
export const useIsDesktop = (): boolean => {
  return useMediaQuery('(min-width: 1024px)');
};

/**
 * Hook pour obtenir le mode de layout actuel
 */
export type LayoutMode = 'organizer-desktop' | 'organizer-mobile' | 'tourist-desktop' | 'tourist-mobile';

export const useLayoutMode = (isOrganizerRoute: boolean): LayoutMode => {
  const isDesktop = useIsDesktop();

  if (isDesktop) {
    return isOrganizerRoute ? 'organizer-desktop' : 'tourist-desktop';
  }
  return isOrganizerRoute ? 'organizer-mobile' : 'tourist-mobile';
};

export default useMediaQuery;
